import { useEffect, useState, useCallback } from 'react';

// Story 065 — Install state for the player PWA.
//
// Captures `beforeinstallprompt` on Chromium-based browsers and exposes a
// `useInstall()` hook used by both the install banner and the user-menu
// "Install app" entry. iOS Safari does not fire this event — there is no
// programmatic install on iOS. We detect iOS Safari and surface the same
// "Install" affordance that opens an instructions modal instead.
//
// Dismissal is per-device via localStorage. Install state (`installed`) is
// detected from `display-mode: standalone` (or `navigator.standalone` on
// iOS) — when the app is launched from the home screen, we treat it as
// installed and hide all install affordances.

const DISMISS_KEY = 'swissmagic.install.dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
// Sticky in-session flag: true once `appinstalled` has fired in this
// tab. The original browser tab's display-mode stays 'browser' even
// after the user installs (Chrome installs without re-launching), so
// reading display-mode alone never flips the banner off in the tab
// the user installed from. This flag bridges the gap until the next
// hard refresh / standalone launch.
let appJustInstalled = false;
const subscribers = new Set<() => void>();

function notify() {
  for (const cb of subscribers) cb();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    appJustInstalled = true;
    notify();
  });
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (appJustInstalled) return true;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari uses navigator.standalone instead of display-mode.
  if ((window.navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }
  return false;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  if (!isIos) return false;
  // CriOS = Chrome iOS, FxiOS = Firefox iOS, both still WebKit but the
  // Add-to-Home-Screen path is Safari-only. Treat them the same as Safari
  // — the modal explains the Safari requirement.
  return true;
}

// True when the primary input is touch — phones, tablets, touch-first
// Chromebooks. Desktop with a touchscreen (e.g. Surface laptops) stays
// false because the user's primary input is the trackpad/mouse.
//
// The install banner + Install-app menu entry only show when this is
// true: a player app installed to the home screen is the killer
// affordance on touch devices, but on a desktop the address-bar
// install icon (Chrome) is the conventional path and our banner just
// adds noise.
function isTouchPrimary(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

function readDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed(v: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (v) localStorage.setItem(DISMISS_KEY, 'true');
    else localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* storage disabled / quota — best effort */
  }
}

export interface InstallState {
  // True when the page is running standalone (already installed) — banner +
  // menu entry should be hidden.
  installed: boolean;
  // True when we have a captured beforeinstallprompt OR we're on iOS Safari
  // — i.e., something useful happens if the user clicks Install.
  canInstall: boolean;
  // True when we should show the iOS modal instead of the native prompt.
  needsIosModal: boolean;
  // True when the user previously chose "don't show again" on the banner.
  // The user-menu "Install app" entry is unaffected by this.
  dismissed: boolean;
  // True when the primary input is touch (phones / tablets). Drives
  // visibility of both the install banner and the user-menu Install-app
  // entry — desktop users have the browser's address-bar install icon
  // and don't need our affordance.
  touchPrimary: boolean;
}

function readState(): InstallState {
  const installed = isStandalone();
  const touchPrimary = isTouchPrimary();
  if (installed) {
    return {
      installed,
      canInstall: false,
      needsIosModal: false,
      dismissed: false,
      touchPrimary,
    };
  }
  const ios = isIosSafari();
  const canInstall = deferredPrompt !== null || ios;
  return {
    installed: false,
    canInstall,
    needsIosModal: ios,
    dismissed: readDismissed(),
    touchPrimary,
  };
}

export function useInstall(): InstallState & {
  install: () => Promise<'accepted' | 'dismissed' | 'instructions-modal'>;
  dismissBanner: () => void;
} {
  const [state, setState] = useState<InstallState>(() => readState());

  useEffect(() => {
    const refresh = () => setState(readState());
    subscribers.add(refresh);
    // Also refresh when the standalone media query changes (the user may
    // install while the page is open).
    const mql = window.matchMedia?.('(display-mode: standalone)');
    mql?.addEventListener?.('change', refresh);
    return () => {
      subscribers.delete(refresh);
      mql?.removeEventListener?.('change', refresh);
    };
  }, []);

  const install = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'instructions-modal'
  > => {
    if (deferredPrompt) {
      const evt = deferredPrompt;
      // beforeinstallprompt is single-use per page load on Chromium. After
      // .prompt() resolves the event is consumed; we null it out and will
      // re-capture on the next page load.
      deferredPrompt = null;
      notify();
      await evt.prompt();
      const choice = await evt.userChoice;
      return choice.outcome;
    }
    // No native prompt available — show instructions. The modal adapts
    // its copy to iOS Safari vs other-browser cases.
    return 'instructions-modal';
  }, []);

  const dismissBanner = useCallback(() => {
    writeDismissed(true);
    setState(readState());
  }, []);

  return { ...state, install, dismissBanner };
}

// Test-only: clear in-memory state. Not used by app code.
export function _resetInstallStateForTests() {
  deferredPrompt = null;
  writeDismissed(false);
  notify();
}
